import {
  ComposeDocumentType,
  PageRange,
  RotationDetails,
  SourcePage,
} from '../types/compose-types';

const pageRangesRegex = /^[0-9]+(?:(?:\s*,\s*|-)[0-9]+)*$/;

export function buildPageRange(
  pages: Array<SourcePage>,
  documentId: string
): string {
  let pageList = '';
  const selectedPages = pages.filter(
    page => page.selected && page.documentId === documentId
  );
  selectedPages.sort((a, b) => (a.selectedOrder > b.selectedOrder ? 1 : -1));
  let i = 0;
  for (i = 0; i < selectedPages.length; i++) {
    if (i + 1 < selectedPages.length) {
      pageList = pageList.concat(selectedPages[i].pageNumber.toString());
      let y;
      const startPageRange = selectedPages[i].pageNumber;
      let pageRangeSize = 1;
      for (y = i + 1; y < selectedPages.length; y++) {
        const nextPageNumber = selectedPages[y].pageNumber;
        if (nextPageNumber === startPageRange + pageRangeSize) {
          pageRangeSize++;
        } else {
          y = selectedPages.length;
        }
      }
      if (pageRangeSize > 1) {
        pageList = pageList.concat(
          '-' + (selectedPages[i].pageNumber + (pageRangeSize - 1)).toString()
        );
        i = i + (pageRangeSize - 1);
        if (i < selectedPages.length - 1) {
          pageList = pageList.concat(',');
        }
      } else {
        pageList = pageList.concat(',');
      }
    } else {
      pageList = pageList.concat(selectedPages[i].pageNumber.toString());
    }
  }
  return pageList;
}

export function isSelectedPage(
  selectedPages: Array<SourcePage>,
  documentId: string,
  pageNumber: number
): boolean {
  const selectedPage = selectedPages.find(
    documentPage =>
      documentPage.documentId === documentId &&
      documentPage.pageNumber === pageNumber &&
      documentPage.selected
  );
  return selectedPage !== undefined;
}

export function hasValidPageRanges(composeDoc: ComposeDocumentType): boolean {
  for (let i = 0; i < composeDoc.sourceDocuments.length; i++) {
    const sourceDoc = composeDoc.sourceDocuments[i];
    if (
      !isValidPageRangesFormat(sourceDoc.selectedPages) ||
      !isValidPageRangesText(
        sourceDoc.selectedPages,
        sourceDoc.sourcePages.length
      )
    ) {
      return false;
    }
  }
  return true;
}

export function isValidPageRange(
  pageRangesText: string,
  totalPages: number
): boolean {
  return (
    isValidPageRangesFormat(pageRangesText) &&
    isValidPageRangesText(pageRangesText, totalPages)
  );
}

export function isValidPageRangesFormat(pageRangesText: string): boolean {
  return pageRangesRegex.test(pageRangesText);
}

export function isValidPageRangesText(
  pageRangesText: string,
  endPageNumber: number
): boolean {
  const selectedPageRangesArray = pageRangesText.split(',');
  let i;
  for (i = 0; i < selectedPageRangesArray.length; i++) {
    const pageRange = getPageRange(selectedPageRangesArray[i]);
    if (pageRange.endPage) {
      if (
        pageRange.startPage > pageRange.endPage ||
        pageRange.startPage > endPageNumber ||
        pageRange.endPage > endPageNumber ||
        pageRange.startPage === pageRange.endPage
      ) {
        return false;
      }
    } else {
      if (pageRange.startPage > endPageNumber) {
        return false;
      }
    }
  }
  return true;
}

export function getPageRange(pageRangesText: string): PageRange {
  const dashIndex = pageRangesText.indexOf('-');
  if (dashIndex > -1) {
    const pageRangeText = pageRangesText;
    const startPage = parseInt(pageRangeText.substring(0, dashIndex), 10);
    const endPage = parseInt(pageRangeText.substring(dashIndex + 1), 10);
    return { startPage, endPage };
  } else {
    const startPage = parseInt(pageRangesText, 10);
    return { startPage };
  }
}

export function getRotationDetails(
  sourcePages: Array<SourcePage>,
  documentId: string,
  pageRange: PageRange
): Array<RotationDetails> {
  const rotationDetails: Array<RotationDetails> = [];
  if (pageRange.endPage) {
    for (let k = pageRange.startPage; k < pageRange.endPage; k++) {
      const sourcePage = sourcePages.find(
        page => page.documentId === documentId && page.pageNumber === k
      );
      if (sourcePage && sourcePage.rotation > 0) {
        rotationDetails.push({
          startPage: sourcePage.pageNumber,
          endPage: sourcePage.pageNumber,
          rotationDegrees: sourcePage.rotation,
        });
      }
    }
  } else {
    const sourcePage = sourcePages.find(
      page =>
        page.documentId === documentId &&
        page.pageNumber === pageRange.startPage
    );
    if (sourcePage && sourcePage.rotation > 0) {
      rotationDetails.push({
        startPage: sourcePage.pageNumber,
        endPage: sourcePage.pageNumber,
        rotationDegrees: sourcePage.rotation,
      });
    }
  }
  return rotationDetails;
}
